import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, useTheme } from '@mui/material';
import { orange } from '@mui/material/colors';
import React, { useEffect, useMemo, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import { SubscriptionStatusDto } from '../../../generated-types/subscriptionStatusDto';
import useApi from '../../../lib/useApi';
import InfoBlock from '../../InfoBlock/InfoBlock';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import ErrorInfoBlock from './ErrorInfoBlock';

/**
 * LicenseRenewalInfoBlock Component
 *
 * Fetches and displays the license renewal date or prompts the user to set up renewal
 * if the subscription is not set to renew.
 */
const LicenseRenewalInfoBlock: React.FC = () => {
  const [subStatus, setSubStatus] = useState<SubscriptionStatusDto | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<boolean>(false);
  const managementApi = useApi(ManagementApi);
  const theme = useTheme();
  const [subtitle, setSubtitle] = useState(
    'All licenses will be automatically renewed on this date.'
  );
  const [color, setColor] = useState(theme.palette.primary.main);
  const [icon, setIcon] = useState(
    <EventRepeatRoundedIcon sx={{ height: '28px', width: '28px' }} />
  );

  useEffect(() => {
    const fetchLicenseRenewalDate = async () => {
      setIsLoading(true);
      setError(false);
      try {
        const status = await managementApi.GetSubscriptionStatus();
        setSubStatus(status);

        // Set the states
        if (!status.active) {
          setSubtitle(
            'Your licenses have expired. Please renew to maintain access.'
          );
          setColor(theme.palette.error.main);
          setIcon(
            <ErrorOutlineRoundedIcon sx={{ height: '28px', width: '28px' }} />
          );
        } else if (!status.willRenew) {
          setSubtitle(
            'Your licenses expire on this date and aren’t set to renew automatically. Please renew to keep access.'
          );
          setColor(orange.A400);
          setIcon(
            <WarningAmberRoundedIcon sx={{ height: '28px', width: '28px' }} />
          );
        }
      } catch (err) {
        console.error('Error fetching subscription end details:', err);
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLicenseRenewalDate();
  }, [managementApi, theme.palette.error.main]);

  const formattedDate = useMemo(() => {
    if (subStatus == null) return '(Loading...)';

    return subStatus.endOfPeriod.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [subStatus]);

  return (
    <Box position="relative" height="100%" width="100%">
      {!error ? (
        <InfoBlock
          baseId="yearly-lapse"
          title="License Renewal"
          content={formattedDate}
          icon={icon}
          subtitle={subtitle}
          color={color}
        />
      ) : (
        <ErrorInfoBlock
          baseId="license-renewal-error"
          title="License Renewal"
          subtitle="Failed to load the renewal date."
        />
      )}
      <LoadingCircle isLoading={isLoading} hideText loadingDelayMs={0} />
    </Box>
  );
};

export default LicenseRenewalInfoBlock;
